/**
 * @file components/shared/UpgradeModal.jsx
 * @description Premium upgrade modal — redirects to Stripe checkout.
 */

import { Camera, Check, Crown, Sparkles, X, Zap } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../utils/AuthContext";
import { openCheckout, isPaymentConfigured } from "../../services/payment";
import { PLANS } from "../../services/subscription";

export function UpgradeModal({ onClose, reason, onShowAuth }) {
  const { user, isGuest } = useAuth();
  const [planType, setPlanType] = useState("monthly");

  const reasonMessages = {
    limit_reached: `You've used all ${PLANS.free.aiCallsPerDay} free AI analyses for today.`,
    photo_premium: "Photo analysis is a Premium feature.",
    history: "Full meal history requires Premium.",
    export: "Data export is a Premium feature. Download all your meals and weight logs as CSV.",
    default: "Unlock the full power of diet-reg.",
  };

  function handleCheckout() {
    if (isGuest) {
      onClose();
      onShowAuth?.();
      return;
    }
    if (!user?.uid) return;
    openCheckout(user.uid, planType);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-brand-surface border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/5 text-gray-400 hover:text-white transition-colors z-10"
        >
          <X size={18} />
        </button>

        <>
          <div className="relative px-6 pt-8 pb-6 bg-gradient-to-br from-brand-green/20 via-emerald-500/10 to-transparent">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 rounded-xl bg-brand-green/20">
                <Crown size={22} className="text-brand-green" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Upgrade to Premium</h2>
                <p className="text-xs text-gray-400">Unlock everything, no limits</p>
              </div>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed">
              {reasonMessages[reason] || reasonMessages.default}
            </p>
          </div>

          <div className="px-6 py-4">
            <div className="space-y-3">
              {[
                { icon: Zap, text: "Unlimited AI meal analysis" },
                { icon: Camera, text: "Photo-powered nutrition scanning" },
                { icon: Sparkles, text: "Full history & data export" },
                { icon: Crown, text: "Priority AI model (most accurate)" },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-brand-green/10">
                    <Icon size={14} className="text-brand-green" />
                  </div>
                  <span className="text-sm text-gray-300">{text}</span>
                  <Check size={14} className="text-brand-green ml-auto" />
                </div>
              ))}
            </div>
          </div>

          {/* Plan toggle */}
          <div className="px-6 pb-2">
            <div className="flex gap-2 p-1 bg-white/5 rounded-xl">
              <button
                onClick={() => setPlanType("monthly")}
                style={{ touchAction: "manipulation" }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${
                  planType === "monthly"
                    ? "bg-brand-green text-brand-bg"
                    : "text-gray-400"
                }`}
              >
                $3.99/mo
              </button>
              <button
                onClick={() => setPlanType("yearly")}
                style={{ touchAction: "manipulation" }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${
                  planType === "yearly"
                    ? "bg-brand-green text-brand-bg"
                    : "text-gray-400"
                }`}
              >
                $39.99/yr
                <span className="ml-1 text-[10px] opacity-70">Save 33%</span>
              </button>
            </div>
          </div>

          <div className="px-6 pb-6 space-y-3 pt-4">
            <button
              onClick={handleCheckout}
              disabled={!isGuest && !isPaymentConfigured}
              style={{ touchAction: "manipulation" }}
              className="w-full py-4 rounded-2xl bg-brand-green hover:bg-emerald-400 disabled:opacity-50 text-brand-bg font-bold text-sm uppercase tracking-widest transition-all active:scale-[0.98] shadow-[0_0_20px_rgba(16,185,129,0.3)]"
            >
              {isGuest ? "Sign in to Subscribe" : isPaymentConfigured ? "Continue to Checkout" : "Coming Soon"}
            </button>

            <button
              onClick={onClose}
              style={{ touchAction: "manipulation" }}
              className="w-full py-3 text-xs text-gray-500 font-medium hover:text-gray-400 transition-colors"
            >
              Maybe later
            </button>

            <p className="text-[10px] text-gray-600 text-center">
              Secure checkout powered by Lemon Squeezy. Cancel anytime.
            </p>
          </div>
        </>

      </div>
    </div>
  );
}